import React from 'react';
import { pick } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';
import {
    Business,
    CompareArrows,
    FilterList,
    Map as MapIcon,
    Notifications,
    Place,
    Visibility
} from '@mui/icons-material';

import {
    useChangeable,
    FullForm,
    AutocompleteField,
    BoolField,
    TextField,
    RangeField
} from '../CommonComponents/Form';

import SelectField from '../CommonComponents/Form/fields/SelectField';
import { InfoText, SectionDivider } from '../CommonComponents';
import ShapeAreaEditor from '../CommonComponents/GoogleMap/ShapeAreaEditor';
import type { AreaShape } from '../CommonComponents/GoogleMap/ShapeAreaEditor';

import {
    createWatchlist,
    deleteWatchlist,
    getWatchlist,
    updateWatchlist
} from '../../api/watchlists';
import { getTenantRoutes } from '../../api/tenantRoutes';
import { getTenantProfile } from '../../api/tenants';
import { searchCompanies } from '../../api-public/company';

import toast from '../../utils/toast';
import {
    circleRoutePoints, polygonToCircle, routeColor, squareRoutePoints
} from '../../utils/tenantRoutes';
import { fetchAllGroups, selectMyGroups } from '../../redux/actions/groups';
import { useDispatch } from '../../redux/hooks';
import { marketFilterLimits } from '../../redux/slices/market';
import type {
    TenantRoute,
    TenantRoutePoint,
    WatchlistAreaType,
    WatchlistMatchMode,
    WatchlistPrefillType,
    WatchlistType
} from '../../types';

import {
    NOTIFICATION_MODE_HELP_TEXTS,
    WatchlistNotificationMode,
    getNotificationMode,
    applyTypeRules
} from './formatWatchlist';
import MatchModePicker from './parts/MatchModePicker';
import NotificationModeControl from './parts/NotificationModeControl';

type AreaMode = 'none' | 'route' | 'own';

const DEFAULT_RADIUS_M = 50000;
const POLYGON_MAP_ROUTE_ID = 1;

const AREA_MODE_OPTIONS: { value: AreaMode, label: string }[] = [
    { value: 'none', label: 'Inget, hela landet' },
    { value: 'route', label: 'Företagets rutt' },
    { value: 'own', label: 'Eget område' }
];

/** Form state; `areaMode` and the shape fields become the API's routeId / area in toRequestBody. */
function toFormState(w: WatchlistPrefillType & Partial<Pick<WatchlistType, 'id' | 'source'>>) {
    const detachedRoute = !w.routeId && !w.area && (w.source === 'route' || w.source === 'residence');
    let areaMode: AreaMode = 'none';
    let areaShape: AreaShape = 'polygon';
    if (w.routeId || detachedRoute) {
        areaMode = 'route';
    } else if (w.area) {
        areaMode = 'own';
        areaShape = w.area.type;
    }

    return {
        ...w,
        areaMode,
        areaShape,
        routeId: w.routeId ?? null,
        matchMode: w.matchMode ?? 'within',
        polygonPoints: w.area?.type === 'polygon' ? w.area.shape.points : null,
        circleCenter: w.area?.type === 'circle' ? w.area.shape.center : null,
        circleRadiusM: w.area?.type === 'circle' ? w.area.shape.radius : DEFAULT_RADIUS_M
    };
}

const defaultFormState = {
    ...toFormState({}),
    name: '',
    isJob: true,
    isCapacity: false, // Capacity is off by default (LL-415)
    takesOffer: false,
    hasListPrice: false,
    groupId: null,
    groupName: '',
    publisherTenantId: null,
    publisherTenantName: '',
    instantNotifications: true,
    dailyNotifications: false
};

function toArea(entity: any): WatchlistAreaType | null {
    if (entity.areaMode === 'own' && entity.areaShape === 'polygon' && entity.polygonPoints) {
        return { type: 'polygon', shape: { points: entity.polygonPoints } };
    }
    if (entity.areaMode === 'own' && entity.areaShape === 'circle' && entity.circleCenter) {
        return {
            type: 'circle',
            shape: { center: entity.circleCenter, radius: Math.round(entity.circleRadiusM) }
        };
    }
    return null;
}

/** Av = paused (the channel flags are kept as they were), Direkt/Dagligen = active with exactly that channel. */
function toNotificationFields(entity: any, mode: WatchlistNotificationMode | null) {
    if (mode === 'off') {
        return {
            isActive: false,
            instantNotifications: !!entity.instantNotifications,
            dailyNotifications: !!entity.dailyNotifications
        };
    }
    if (mode === null) {
        // Active without a channel: unchanged
        return { isActive: true, instantNotifications: false, dailyNotifications: false };
    }
    return {
        isActive: true,
        instantNotifications: mode === 'instant',
        dailyNotifications: mode === 'daily'
    };
}

/** The area is always sent: routeId + area both null is the deliberate "whole country". */
function toRequestBody(entity: any, mode: WatchlistNotificationMode | null) {
    return {
        ...toNotificationFields(entity, mode),
        ...pick(entity, [
            'name', 'isJob', 'isCapacity', 'takesOffer', 'groupId', 'hasListPrice', 'matchMode',
            'minGrossWeight', 'maxGrossWeight', 'minChargeableWeight', 'maxChargeableWeight', 'minVolume', 'maxVolume',
            'minLoadingMeters', 'maxLoadingMeters', 'minPalletPlaces', 'maxPalletPlaces', 'minPallets', 'maxPallets'
        ]),
        publisherTenantId: entity.publisherTenantId ?? null,
        routeId: entity.areaMode === 'route' ? entity.routeId : null,
        area: toArea(entity)
    };
}

function getAreaError(entity: any): string | null {
    if (entity.areaMode === 'route' && !entity.routeId) {
        return 'Välj en av företagets rutter, eller välj ett annat områdesalternativ.';
    }
    if (entity.areaMode === 'own' && entity.areaShape === 'polygon' && (entity.polygonPoints?.length ?? 0) < 3) {
        return 'Rita ett område på kartan.';
    }
    if (entity.areaMode === 'own' && entity.areaShape === 'circle' && !entity.circleCenter) {
        return 'Välj en plats för cirkeln.';
    }
    return null;
}

/** Both shapes stay in the form state; a shape not set yet is seeded from the other one (or the home). */
function getShapeDefaults(entity: any, shape: AreaShape, home: { lat?: number | null, lng?: number | null }) {
    if (shape === 'polygon' && !entity.polygonPoints) {
        return {
            polygonPoints: entity.circleCenter
                ? circleRoutePoints(entity.circleCenter.lat, entity.circleCenter.lng, entity.circleRadiusM)
                : squareRoutePoints(home.lat, home.lng)
        };
    }
    if (shape === 'circle' && !entity.circleCenter && entity.polygonPoints?.length) {
        const { center, radiusM } = polygonToCircle(entity.polygonPoints);
        return { circleCenter: center, circleRadiusM: radiusM };
    }
    return {};
}

/** Create/edit form. Can be prefilled on create: navigate('/watchlists/create', { state: { prefill } }). */
export default function WatchlistForm() {
    const { id } = useParams();
    const location = useLocation();
    const prefill = (location.state as { prefill?: WatchlistPrefillType } | null)?.prefill;
    const [entity, onChange] = useChangeable({ ...defaultFormState, ...(prefill ? toFormState(prefill) : {}) });
    const onTypeChange = React.useCallback((change: { isJob?: boolean, isCapacity?: boolean, takesOffer?: boolean }) => {
        onChange(applyTypeRules(change));
    }, [onChange]);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const groups = useSelector(selectMyGroups());
    const [routes, setRoutes] = React.useState<TenantRoute[]>([]);
    const [home, setHome] = React.useState<{ lat?: number | null, lng?: number | null }>({});
    const [mode, setMode] = React.useState<WatchlistNotificationMode | null>(() => (
        getNotificationMode({ isActive: true, ...defaultFormState, ...prefill }) ?? 'instant'
    ));

    React.useEffect(() => {
        (async () => {
            if (id) {
                const watchlist = await getWatchlist(id);
                onChange(toFormState(watchlist));
                setMode(getNotificationMode(watchlist));
            }
        })();
    }, [id, onChange]);

    React.useEffect(() => {
        (async () => {
            try {
                setRoutes(await getTenantRoutes());
            } catch (e) {
                toast(e, 'Kunde inte hämta företagets rutter');
            }
        })();
    }, []);

    React.useEffect(() => {
        getTenantProfile().then((p) => setHome({ lat: p?.lat, lng: p?.lng })).catch(() => undefined);
    }, []);

    const fetchGroups = React.useCallback(() => {
        dispatch(fetchAllGroups());
    }, [dispatch]);

    const setAreaMode = React.useCallback((areaMode: AreaMode) => {
        const changes: any = { areaMode };
        if (areaMode === 'own') {
            Object.assign(changes, getShapeDefaults(entity, entity.areaShape, home));
        }
        onChange(changes);
    }, [entity, home, onChange]);

    const setAreaShape = React.useCallback((areaShape: AreaShape) => {
        onChange({ areaShape, ...getShapeDefaults(entity, areaShape, home) });
    }, [entity, home, onChange]);

    const onCircleChange = React.useCallback((circleCenter: TenantRoutePoint, circleRadiusM: number) => {
        onChange({ circleCenter, circleRadiusM });
    }, [onChange]);

    const onSubmit = React.useCallback(async () => {
        const areaError = getAreaError(entity);
        if (areaError) {
            toast(areaError);
            return;
        }
        try {
            const body = toRequestBody(entity, mode);
            if (id) {
                await updateWatchlist(id, body);
                toast('Bevakning uppdaterad');
                navigate(-1);
            } else {
                const createdWatchlist = await createWatchlist(body);
                toast('Bevakning skapad');
                navigate(`/watchlists/${createdWatchlist.id}`, { replace: true });
            }
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} bevakningen, försök igen`);
        }
    }, [id, entity, mode, navigate]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteWatchlist(id);
                toast('Bevakning borttagen');
                navigate('/watchlists', { replace: true });
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort bevakningen, försök igen');
        }
    }, [id, navigate]);

    const selectedRoute = routes.find((r) => r.id === entity.routeId);
    const mapRoutes: TenantRoute[] = React.useMemo(() => {
        if (entity.areaMode === 'own' && entity.areaShape === 'polygon' && entity.polygonPoints) {
            return [{ id: POLYGON_MAP_ROUTE_ID, name: 'Område', points: entity.polygonPoints }];
        }
        return entity.areaMode === 'route' && selectedRoute ? [selectedRoute] : [];
    }, [entity.areaMode, entity.areaShape, entity.polygonPoints, selectedRoute]);

    const onPolygonChange = React.useCallback((_id: number, points: TenantRoutePoint[]) => {
        onChange({ polygonPoints: points });
    }, [onChange]);

    const leftSx = { gridColumn: { lg: 1 } };
    const isOwnCircle = entity.areaMode === 'own' && entity.areaShape === 'circle';

    const rangeFields = [
        ['grossWeight', 'Totalvikt [kg]'],
        ['chargeableWeight', 'FDR-vikt [kg]'],
        ['loadingMeters', 'Flakmeter'],
        ['volume', 'Volym [m³]'],
        ['palletPlaces', 'Pallplatser'],
        ['pallets', 'Pallar']
    ];

    return (
        <FullForm
            title={`${entity.id ? 'Redigera' : 'Skapa'} bevakning`}
            entity={entity}
            onSubmit={onSubmit}
            onDelete={entity.id ? onDelete : undefined}
            showBottomSave
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
                    columnGap: 3,
                    alignItems: 'start'
                }}
            >
                <Box sx={leftSx}>
                    <SectionDivider label="Bevakning" icon={<Visibility fontSize="small" />} marginTop={1} />
                    <Grid container spacing={2}>
                        {!id && entity.hadManualRequirements && (
                            <Grid item xs={12}>
                                <InfoText>
                                    Bevakningar matchar alltid mot företagets profil. Ni kommer bara få notiser där ni uppfyller kraven för uppdraget.
                                </InfoText>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label="Namn"
                                value={entity.name}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormLabel component="legend" sx={{ mb: 0.5 }}>Uppdrag</FormLabel>
                            <BoolField
                                name="isJob"
                                alignItems="center"
                                label="Uppdrag"
                                value={entity.isJob}
                                onChange={onTypeChange}
                            />
                            <BoolField
                                name="takesOffer"
                                alignItems="center"
                                label="Bara uppdrag som tar intresseanmälningar"
                                value={entity.takesOffer}
                                disabled={!entity.isJob}
                                onChange={onTypeChange}
                            />
                            <BoolField
                                name="isCapacity"
                                alignItems="center"
                                label="Kapacitet"
                                value={entity.isCapacity}
                                onChange={onTypeChange}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={leftSx}>
                    <SectionDivider label="Notiser" icon={<Notifications fontSize="small" />} />
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm>
                            <Typography variant="subtitle2">Hur vill du bli notifierad?</Typography>
                            {mode && (
                                <Typography variant="body2" color="text.secondary">
                                    {NOTIFICATION_MODE_HELP_TEXTS[mode]}
                                </Typography>
                            )}
                        </Grid>
                        <Grid item xs={12} sm="auto">
                            <NotificationModeControl
                                value={mode}
                                onSelect={setMode}
                                label="Notiser för bevakningen"
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Box sx={leftSx}>
                    <SectionDivider label="Område" icon={<Place fontSize="small" />} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl>
                                <FormLabel id="watchlist-area-mode">Bevaka</FormLabel>
                                <RadioGroup
                                    aria-labelledby="watchlist-area-mode"
                                    value={entity.areaMode}
                                    onChange={(e) => setAreaMode(e.target.value as AreaMode)}
                                >
                                    {AREA_MODE_OPTIONS.map((o) => (
                                        <FormControlLabel
                                            key={o.value}
                                            value={o.value}
                                            control={<Radio size="small" />}
                                            label={o.label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>

                        {entity.areaMode === 'route' && (
                            <Grid item xs={12}>
                                <SelectField
                                    name="routeId"
                                    label="Rutt"
                                    required
                                    value={entity.routeId ?? ''}
                                    options={routes.map((r) => ({ value: r.id, label: r.name }))}
                                    helperText={routes.length === 0 ? 'Företaget har inga rutter ännu.' : ''}
                                    onChange={onChange}
                                />
                            </Grid>
                        )}

                        {entity.areaMode === 'own' && entity.areaShape === 'polygon' && (
                            <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                    Välj form i kartan. Dra i hörnen och punkterna på kartan för att forma området.
                                </Typography>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <InfoText>
                                Bevakningen följer företagets profil: bara uppdrag som ert företags fordonstyper,
                                utrustning och certifikat uppfyller kraven för matchar.
                            </InfoText>
                        </Grid>
                    </Grid>
                </Box>

                {/* Sticky right column on large screens, pinned to row 1 (after Område on small screens) */}
                <Box
                    sx={{
                        gridColumn: { lg: 2 },
                        gridRow: { lg: '1 / span 8' },
                        position: { lg: 'sticky' },
                        top: { lg: 16 },
                        mb: 2
                    }}
                >
                    <SectionDivider label="Karta" icon={<MapIcon fontSize="small" />} marginTop={1} />
                    <ShapeAreaEditor
                        shape={isOwnCircle ? 'circle' : 'polygon'}
                        onShapeChange={setAreaShape}
                        showToggle={entity.areaMode === 'own'}
                        routes={mapRoutes}
                        selectedId={entity.areaMode === 'own' ? POLYGON_MAP_ROUTE_ID : null}
                        fitToId={entity.areaMode === 'own' ? POLYGON_MAP_ROUTE_ID : entity.routeId}
                        onPointsChange={onPolygonChange}
                        circleCenter={entity.circleCenter}
                        circleRadiusM={entity.circleRadiusM}
                        onCircleChange={onCircleChange}
                        otherRoutes={routes.map((route, index) => ({ ...route, color: routeColor(index) }))}
                        height="clamp(260px, 45vh, 640px)"
                    />

                    {entity.areaMode !== 'none' && (
                        <>
                            <SectionDivider label="Matchning" icon={<CompareArrows fontSize="small" />} />
                            <MatchModePicker
                                label="Matchning"
                                value={entity.matchMode}
                                onChange={(matchMode: WatchlistMatchMode) => onChange({ matchMode })}
                            />
                        </>
                    )}
                </Box>

                <Box sx={leftSx}>
                    <SectionDivider label="Filter" icon={<FilterList fontSize="small" />} />
                    <Grid container spacing={2}>
                        {rangeFields.map(([name, label]) => {
                            const key = name.charAt(0).toUpperCase() + name.slice(1);
                            const limits: any = marketFilterLimits;
                            return (
                                <Grid item xs={12} key={name}>
                                    <RangeField
                                        compact
                                        showInputFields
                                        name={name}
                                        label={label}
                                        minValue={limits[`min${key}`]}
                                        maxValue={limits[`max${key}`]}
                                        lowValue={entity[`min${key}`] || limits[`min${key}`]}
                                        highValue={entity[`max${key}`] || limits[`max${key}`]}
                                        onChange={([low, high]) => {
                                            onChange({ [`min${key}`]: low, [`max${key}`]: high });
                                        }}
                                    />
                                </Grid>
                            );
                        })}
                    </Grid>
                </Box>

                <Box sx={leftSx}>
                    <SectionDivider label="Avsändare" icon={<Business fontSize="small" />} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                                label="Endast i grupp"
                                optionIdKey="id"
                                defaultOption={!entity.groupId ? null : {
                                    id: entity.groupId,
                                    name: entity.groupName
                                }}
                                fetchOptions={fetchGroups}
                                options={groups.filter(
                                    (o: { isOwner: boolean; isAccepted: boolean; }) => (o.isOwner || o.isAccepted)
                                )}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.ownerTenantName || ''}
                                onChange={(group) => onChange({
                                    groupId: group ? group.id : null,
                                    groupName: group ? group.name : null
                                })}
                                fetchOnlyOnce
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteField<{ id: number, name: string, orgNumber?: string }>
                                label="Avsändare (valfritt)"
                                helperText="Bara uppdrag där avsändaren är synlig matchar."
                                optionIdKey="id"
                                defaultOption={!entity.publisherTenantId ? null : {
                                    id: entity.publisherTenantId,
                                    name: entity.publisherTenantName || `Företag ${entity.publisherTenantId}`
                                }}
                                fetchOptions={searchCompanies}
                                minQueryLength={3}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.orgNumber || ''}
                                onChange={(company) => onChange({
                                    publisherTenantId: company ? company.id : null,
                                    publisherTenantName: company ? company.name : ''
                                })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <BoolField
                                name="hasListPrice"
                                label="Endast uppdrag med riktpris"
                                value={entity.hasListPrice}
                                onChange={({ hasListPrice }) => onChange({ hasListPrice })}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </FullForm>
    );
}
